import { m } from 'motion/react';
import { closeMiniApp } from '@telegram-apps/sdk-react';
import type { Me } from '../api';
import { fmtNum, fmtPct, t } from '../format';
import { haptic } from '../telegram';
import { CarHero } from '../ui/CarHero';
import { Icon } from '../ui/icons';
import { Odometer } from '../ui/Odometer';
import { TicketCard } from '../ui/TicketCard';

const DAY = 86_400_000;

function drawLabel(drawAt: string, now = Date.now()) {
  const days = Math.ceil((new Date(drawAt).getTime() - now) / DAY);
  if (days <= 0) return t('app.home.drawToday');
  if (days === 1) return t('app.home.drawInOne');
  return t('app.home.drawIn', { days });
}

function seasonWeek(startsAt: string, endsAt: string, now = Date.now()) {
  const start = new Date(startsAt).getTime();
  const end = new Date(endsAt).getTime();
  const weeks = Math.max(1, Math.round((end - start) / (7 * DAY)));
  const week = Math.min(weeks, Math.max(1, Math.floor((now - start) / (7 * DAY)) + 1));
  return { week, weeks, progress: Math.min(1, Math.max(0, (now - start) / (end - start))) };
}

const rise = (i: number) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay: 0.12 + i * 0.04, ease: [0.2, 0.8, 0.2, 1] as const },
});

/** Mock builds only: `?revealDay=N` previews the photo reveal on any day (screenshots). */
function previewReveal(r: { day: number; total: number }) {
  if (import.meta.env.VITE_TG_MOCK !== '1') return r;
  const day = Number(new URLSearchParams(location.search).get('revealDay'));
  return day > 0 ? { ...r, day: Math.min(day, r.total) } : r;
}

export function Home({
  me,
  flipKey,
  onProfile,
  onTasks,
  onDraw,
}: {
  me: Me;
  flipKey: number;
  onProfile: () => void;
  onTasks: () => void;
  onDraw: () => void;
}) {
  const total = me.balance.total;
  const sw = seasonWeek(me.season.startsAt, me.season.endsAt);
  const initial = (me.user.firstName ?? me.user.username ?? '·').slice(0, 1).toUpperCase();

  return (
    <div className="pb-[calc(96px+var(--safe-bottom))]">
      <header className="absolute inset-x-0 top-0 z-10 flex items-center justify-between px-4 pt-[calc(12px+var(--safe-top))]">
        <div className="flex items-center gap-2">
          <span className="tabular text-sm text-text">{drawLabel(me.season.drawAt)}</span>
          {me.season.isTest && (
            <span className="rounded-md bg-gold-wash px-1.5 py-0.5 text-xs font-medium text-gold">
              {t('app.testBadge')}
            </span>
          )}
        </div>
        <button
          onClick={() => {
            haptic.tap();
            onProfile();
          }}
          aria-label={t('app.profile.title')}
          className="grid size-9 place-items-center overflow-hidden rounded-full border border-line-strong bg-raised text-sm font-medium active:scale-95"
        >
          {me.user.photoUrl ? (
            <img src={me.user.photoUrl} alt="" className="size-full object-cover" />
          ) : (
            initial
          )}
        </button>
      </header>

      <CarHero reveal={previewReveal(me.user.reveal)} alt={me.season.prizeName} />

      <div className="relative z-10 -mt-14 px-4">
        <m.p {...rise(0)} className="mb-3 text-xs text-muted">
          {t('app.home.prizeLabel')}
        </m.p>
        <m.h1 {...rise(1)} className="display mb-5 text-2xl leading-[0.95] font-semibold text-text">
          {me.season.prizeName}
        </m.h1>

        <m.div {...rise(2)}>
          <TicketCard
            flipKey={flipKey}
            stub={
              <div className="flex h-full flex-col justify-between">
                <span className="text-xs font-medium opacity-60">{t('app.home.rank')}</span>
                <span className="display tabular text-xl font-semibold">
                  {me.rank ? `#${me.rank}` : '—'}
                </span>
              </div>
            }
          >
            {total > 0 ? (
              <>
                <p className="text-sm font-medium opacity-70">{t('app.home.ticketsLabel')}</p>
                <Odometer
                  value={total}
                  format={fmtNum}
                  label={
                    total === 1
                      ? t('app.home.yourTicketsOne')
                      : t('app.home.yourTickets', { n: fmtNum(total) })
                  }
                  className="display mt-1 text-[clamp(3.5rem,19vw,4.5rem)] font-bold [text-shadow:var(--emboss)]"
                />
                <p className="tabular mt-3 text-xs font-medium tracking-wide opacity-60">
                  {me.user.ticketCode}
                </p>
              </>
            ) : (
              <div className="space-y-2 py-1">
                <p className="display text-xl font-semibold">{t('app.home.noTickets')}</p>
                <p className="text-sm leading-snug opacity-75">{t('app.home.noTicketsBody')}</p>
                <button
                  onClick={() => {
                    haptic.tap();
                    closeMiniApp.ifAvailable();
                  }}
                  className="cta-shine mt-1 h-10 rounded-xl bg-foil-ink px-4 text-sm font-medium text-gold-bright active:scale-[0.98]"
                  style={{ ['--shine' as string]: 0.3 }}
                >
                  {t('app.home.getFirst')}
                </button>
              </div>
            )}
          </TicketCard>
        </m.div>

        <m.dl {...rise(3)} className="mt-6 grid grid-cols-2 divide-x divide-line">
          <div className="pr-4">
            <dt className="text-xs text-muted">{t('app.home.chance')}</dt>
            <dd className="display tabular mt-1 text-xl font-semibold text-gold">
              <Odometer value={Math.round(me.chancePct * 100)} format={(n) => fmtPct(n / 100)} />
            </dd>
          </div>
          <div className="pl-4">
            <dt className="text-xs text-muted">{t('app.home.inDraw')}</dt>
            <dd className="display tabular mt-1 text-xl font-semibold text-text">
              <Odometer value={me.participants} format={fmtNum} />
            </dd>
          </div>
        </m.dl>

        {total > 0 && (
          <m.button
            {...rise(4)}
            onClick={() => {
              haptic.tap();
              onTasks();
            }}
            className="cta-shine cta-foil mt-6 flex h-12 w-full items-center justify-between rounded-2xl bg-gold px-5 text-base font-medium text-ink active:scale-[0.98]"
          >
            {t('app.home.boost')}
            {Icon.chevron}
          </m.button>
        )}

        <m.div {...rise(4)} className="mt-6">
          <div className="flex items-baseline justify-between text-xs text-muted">
            <span>{me.season.name}</span>
            <span className="tabular">
              {t('app.home.seasonWeek', { week: sw.week, weeks: sw.weeks })}
            </span>
          </div>
          <div className="mt-2 h-[3px] overflow-hidden rounded-full bg-raised">
            <m.div
              className="h-full rounded-full bg-gold"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: Math.max(0.02, sw.progress) }}
              style={{ originX: 0 }}
              transition={{ duration: 0.9, delay: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
            />
          </div>
        </m.div>

        <m.button
          {...rise(5)}
          onClick={() => {
            haptic.tap();
            onDraw();
          }}
          className="mt-6 flex w-full items-center justify-between rounded-2xl border border-line bg-panel px-4 py-3.5 text-left active:scale-[0.99]"
        >
          <span>
            <span className="block text-sm text-text">{t('app.draw.entry')}</span>
            <span className="tabular block text-xs text-muted">{drawLabel(me.season.drawAt)}</span>
          </span>
          <span className="text-muted">{Icon.chevron}</span>
        </m.button>

        <m.section {...rise(5)} className="mt-8">
          <h2 className="mb-3 text-sm font-medium text-text">{t('app.home.winners')}</h2>
          {me.winners.length === 0 ? (
            <div className="flex items-center gap-4 rounded-2xl border border-line bg-panel p-4">
              <span className="text-gold-deep">
                {me.features?.wheel === false ? Icon.ticket : Icon.wheel}
              </span>
              <p className="text-sm leading-snug text-muted">
                {t(
                  me.features?.wheel === false
                    ? 'app.home.winnersEmptyNoWheel'
                    : 'app.home.winnersEmpty',
                )}
              </p>
            </div>
          ) : (
            <ul className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-1">
              {me.winners.map((w) => (
                <li
                  key={w.id}
                  className="w-40 shrink-0 snap-start rounded-2xl border border-line bg-panel p-3"
                >
                  {w.mediaUrl && (
                    <img
                      src={w.mediaUrl}
                      alt=""
                      className="mb-2 aspect-square w-full rounded-xl object-cover"
                    />
                  )}
                  <p className="truncate text-sm text-text">{w.name}</p>
                  <p className="truncate text-xs text-muted">
                    {[w.city, w.prize].filter(Boolean).join(', ')}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </m.section>
      </div>
    </div>
  );
}
